import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../../config/supabase';
import { formatCurrency } from '../../utils/formatters';
import { VAT_RATE } from '../../utils/costCalculations';
import { UNIT_MAP, getLineTotal as getLineTotalHelper } from '../../utils/boqHelpers';
import { Warning, FloppyDisk, Check, CaretDown, CaretUp } from '@phosphor-icons/react';
import styles from './PublicBOQQuoteView.module.css';

// All available unit options for the vendor to choose from
const UNIT_OPTIONS = Object.entries(UNIT_MAP).map(([name, label]) => ({ name, label }));

/**
 * PublicBOQQuoteView — Vendor-facing page for pricing BOQ line items.
 * Accessed via /quote/boq/:token without authentication.
 * Vendors can:
 *   - Enter line_price per item
 *   - Add line_notes (comments) per item
 *   - Toggle items off (skip = set line_price to null)
 *   - Save all changes
 *
 * Security: Only sees their own quote. RLS enforces isolation via share_token + share_enabled.
 */
const PublicBOQQuoteView = () => {
  const { token } = useParams();

  // Fetch quote by share token (no auth, anon role)
  const {
    data: quote,
    isLoading: quoteLoading,
    error: quoteError,
  } = useQuery({
    queryKey: ['public-boq-quote', token],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('vendor_quotes')
        .select(
          `
          id,
          project_id,
          total_price,
          validity_date,
          notes,
          status,
          created_at,
          master_vendors (
            id,
            name,
            contact_person
          )
        `,
        )
        .eq('share_token', token)
        .eq('share_enabled', true)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!token,
    retry: false,
  });

  // Fetch enabled project BOQ notes (anon RLS allows this for projects with shared quotes)
  const { data: projectNotes } = useQuery({
    queryKey: ['public-boq-notes', quote?.project_id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('project_boq_notes')
        .select('id, text, bb_chapter_id, display_order, reference_bb_chapters(code, name_he)')
        .eq('project_id', quote.project_id)
        .eq('enabled', true)
        .order('display_order', { ascending: true });

      if (error) throw error;
      return data;
    },
    enabled: !!quote?.project_id,
  });

  // Collapsible notes state — expanded by default on first visit
  const [notesExpanded, setNotesExpanded] = useState(true);

  // Group notes by BB chapter for display
  const groupedNotes = useMemo(() => {
    if (!projectNotes || projectNotes.length === 0) return [];

    const map = new Map();
    // "כללי" group for notes without a chapter
    map.set('__general__', { name: 'כללי', code: null, notes: [] });

    projectNotes.forEach(note => {
      const ch = note.reference_bb_chapters;
      if (ch) {
        const key = note.bb_chapter_id;
        if (!map.has(key)) {
          map.set(key, { name: ch.name_he, code: ch.code, notes: [] });
        }
        map.get(key).notes.push(note);
      } else {
        map.get('__general__').notes.push(note);
      }
    });

    // Build ordered array: כללי first, then sorted by chapter code
    const groups = [];
    const general = map.get('__general__');
    if (general.notes.length > 0) groups.push(general);

    // Sort chapter groups by code
    const chapterGroups = [];
    map.forEach((group, key) => {
      if (key !== '__general__' && group.notes.length > 0) chapterGroups.push(group);
    });
    chapterGroups.sort((a, b) =>
      (a.code || '').localeCompare(b.code || '', undefined, { numeric: true }),
    );
    groups.push(...chapterGroups);

    return groups;
  }, [projectNotes]);

  // Fetch quote items (display_data is stored inline — no need to query project_items or reference tables)
  const { data: quoteItems, isLoading: itemsLoading } = useQuery({
    queryKey: ['public-boq-quote-items', quote?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('vendor_quote_items')
        .select('*')
        .eq('quote_id', quote.id);

      if (error) throw error;
      return data;
    },
    enabled: !!quote?.id,
  });

  // Local state for editable fields
  const [localPrices, setLocalPrices] = useState({}); // { quoteItemId: raw number string }
  const [localUnits, setLocalUnits] = useState({}); // { quoteItemId: cost_type_name }
  const [localNotes, setLocalNotes] = useState({}); // { quoteItemId: string }
  const [skippedItems, setSkippedItems] = useState(new Set()); // Set of quoteItemIds
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const [focusedPrice, setFocusedPrice] = useState(null); // track which price input is focused

  // Initialize local state from fetched data (once)
  useEffect(() => {
    if (!quoteItems || initialized) return;
    const prices = {};
    const units = {};
    const notes = {};
    const skipped = new Set();
    quoteItems.forEach(qi => {
      prices[qi.id] = qi.line_price != null ? String(qi.line_price) : '';
      units[qi.id] = qi.display_data?.cost_type_name || '';
      notes[qi.id] = qi.line_notes || '';
      // If price is null and the item has been viewed before (updated_at differs from created_at),
      // it was likely deliberately skipped. For new quotes, everything starts un-skipped.
      if (qi.line_price == null && qi.updated_at !== qi.created_at) {
        skipped.add(qi.id);
      }
    });
    setLocalPrices(prices);
    setLocalUnits(units);
    setLocalNotes(notes);
    setSkippedItems(skipped);
    setInitialized(true);
  }, [quoteItems, initialized]);

  // Group quote items by chapter using snapshotted display_data
  const groupedLineItems = useMemo(() => {
    if (!quoteItems) return [];
    const NO_CHAPTER = '__no_chapter__';
    const groupMap = new Map();

    quoteItems.forEach(qi => {
      const dd = qi.display_data || {};
      const chapterCode = dd.chapter_code || '';
      const chapterName = dd.chapter_name || '';
      const groupKey = chapterCode || NO_CHAPTER;

      if (!groupMap.has(groupKey)) {
        groupMap.set(groupKey, {
          chapterId: groupKey,
          chapterCode,
          chapterName: chapterName || 'ללא פרק',
          items: [],
        });
      }

      groupMap.get(groupKey).items.push({
        quoteItem: qi,
        itemName: dd.item_name || 'פריט',
        unitLabel: dd.unit_label || '—',
        totalQty: dd.quantity || 1,
      });
    });

    return Array.from(groupMap.values()).sort((a, b) => {
      if (a.chapterId === NO_CHAPTER) return 1;
      if (b.chapterId === NO_CHAPTER) return -1;
      return a.chapterCode.localeCompare(b.chapterCode, undefined, { numeric: true });
    });
  }, [quoteItems]);

  // Toggle skip for an item
  const handleToggleSkip = useCallback(quoteItemId => {
    setSkippedItems(prev => {
      const next = new Set(prev);
      if (next.has(quoteItemId)) {
        next.delete(quoteItemId);
      } else {
        next.add(quoteItemId);
      }
      return next;
    });
  }, []);

  // Update local price — store raw number string
  const handlePriceChange = useCallback((quoteItemId, value) => {
    // Strip non-numeric chars (allow digits, dot, comma)
    const raw = value.replace(/[^\d.,]/g, '').replace(',', '.');
    setLocalPrices(prev => ({ ...prev, [quoteItemId]: raw }));
  }, []);

  // Update local unit
  const handleUnitChange = useCallback((quoteItemId, costTypeName) => {
    setLocalUnits(prev => ({ ...prev, [quoteItemId]: costTypeName }));
  }, []);

  // Update local notes
  const handleNotesChange = useCallback((quoteItemId, value) => {
    setLocalNotes(prev => ({ ...prev, [quoteItemId]: value }));
  }, []);

  /**
   * Get the line total for a quote item.
   * Uses shared getLineTotal helper for consistent per-unit logic.
   */
  const getLineTotal = useCallback(
    (quoteItemId, qty) => {
      const priceStr = localPrices[quoteItemId];
      if (!priceStr) return 0;
      const price = parseFloat(priceStr);
      const costType = localUnits[quoteItemId] || '';
      return getLineTotalHelper(price, qty, costType);
    },
    [localPrices, localUnits],
  );

  // Compute totals
  const totals = useMemo(() => {
    if (!quoteItems) return { subtotal: 0, vat: 0, grandTotal: 0 };
    let subtotal = 0;
    quoteItems.forEach(qi => {
      if (skippedItems.has(qi.id)) return;
      const qty = qi.display_data?.quantity || 1;
      subtotal += getLineTotal(qi.id, qty);
    });
    const vat = subtotal * VAT_RATE;
    return { subtotal, vat, grandTotal: subtotal + vat };
  }, [quoteItems, skippedItems, getLineTotal]);

  // Save all changes
  const handleSave = useCallback(async () => {
    if (!quoteItems || !quote) return;
    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      // Update each quote item
      for (const qi of quoteItems) {
        const isSkipped = skippedItems.has(qi.id);
        const priceStr = localPrices[qi.id];
        const price = isSkipped || !priceStr ? null : parseFloat(priceStr);
        const notes = localNotes[qi.id] || null;
        // Persist unit change in display_data
        const updatedDisplayData = {
          ...(qi.display_data || {}),
          cost_type_name: localUnits[qi.id] || qi.display_data?.cost_type_name || '',
          unit_label:
            UNIT_MAP[localUnits[qi.id]] || localUnits[qi.id] || qi.display_data?.unit_label || '',
        };

        const { error } = await supabase
          .from('vendor_quote_items')
          .update({
            line_price: price,
            line_notes: notes,
            display_data: updatedDisplayData,
            updated_at: new Date().toISOString(),
          })
          .eq('id', qi.id);

        if (error) throw error;
      }

      // Update quote total_price (subtotal before VAT) and status
      const { error: quoteUpdateError } = await supabase
        .from('vendor_quotes')
        .update({
          total_price: totals.subtotal > 0 ? totals.subtotal : null,
          status: 'quote_received',
          updated_at: new Date().toISOString(),
        })
        .eq('id', quote.id);

      if (quoteUpdateError) throw quoteUpdateError;

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Error saving quote:', error);
      setSaveError('שגיאה בשמירה. נסה שוב.');
    } finally {
      setIsSaving(false);
    }
  }, [quoteItems, quote, localPrices, localNotes, localUnits, skippedItems, totals]);

  // Check if quote is expired
  const isExpired = useMemo(() => {
    if (!quote?.validity_date) return false;
    return new Date(quote.validity_date) < new Date();
  }, [quote?.validity_date]);

  // Loading state
  if (quoteLoading || itemsLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <span>טוען כתב כמויות...</span>
        </div>
      </div>
    );
  }

  // Error / not found
  if (quoteError || !quote) {
    return (
      <div className={styles.container}>
        <div className={styles.errorCard}>
          <Warning size={48} className={styles.errorIcon} />
          <h1 className={styles.errorTitle}>כתב הכמויות לא נמצא</h1>
          <p className={styles.errorDescription}>
            הקישור אינו תקף או שהשיתוף בוטל.
            <br />
            אנא פנה למנהל הפרויקט לקבלת קישור חדש.
          </p>
        </div>
      </div>
    );
  }

  const vendor = quote.master_vendors;
  const isReadOnly = quote.status === 'selected' || quote.status === 'rejected';

  return (
    <div className={styles.container} dir="rtl">
      {/* Header */}
      <header className={styles.header}>
        <div className={styles.logo}>KAMUYOT</div>
        <div className={styles.headerBadge}>כתב כמויות — תמחור</div>
      </header>

      {/* Main Content */}
      <main className={styles.main}>
        {/* Expired Warning */}
        {isExpired && (
          <div className={styles.expiredBanner}>
            <Warning size={20} />
            <span>תוקף ההצעה פג</span>
          </div>
        )}

        {/* Read-only notice */}
        {isReadOnly && (
          <div className={styles.readOnlyBanner}>
            הצעה זו {quote.status === 'selected' ? 'נבחרה' : 'נדחתה'} ואינה ניתנת לעריכה
          </div>
        )}

        {/* Vendor card */}
        <div className={styles.vendorCard}>
          <h1 className={styles.vendorName}>{vendor?.name || 'ספק'}</h1>
          {/* Project name + date */}
          <div className={styles.quoteMeta}>
            {quoteItems?.[0]?.display_data?.project_name && (
              <span>פרויקט: {quoteItems[0].display_data.project_name}</span>
            )}
            {quote.created_at && (
              <span>
                תאריך:{' '}
                {new Date(quote.created_at).toLocaleDateString('he-IL', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                })}
              </span>
            )}
          </div>
          <p className={styles.instructions}>
            מלא מחיר לכל פריט. ניתן לדלג על פריטים שאינך מעוניין לתמחר באמצעות ה-checkbox.
          </p>
        </div>

        {/* BOQ Notes — collapsible section */}
        {groupedNotes.length > 0 && (
          <div className={styles.notesCard}>
            <div className={styles.notesHeader} onClick={() => setNotesExpanded(prev => !prev)}>
              <span className={styles.notesHeaderTitle}>הערות לכתב כמויות</span>
              <span className={styles.notesHeaderCount}>{projectNotes?.length || 0} הערות</span>
              {notesExpanded ? <CaretUp size={18} /> : <CaretDown size={18} />}
            </div>
            {notesExpanded && (
              <div className={styles.notesBody}>
                {groupedNotes.map((group, gi) => (
                  <div key={gi} className={styles.notesGroup}>
                    <div className={styles.notesGroupTitle}>
                      {group.name}
                      {group.code && <span className={styles.notesGroupCode}> ({group.code})</span>}
                    </div>
                    <ul className={styles.notesList}>
                      {group.notes.map(note => (
                        <li key={note.id} className={styles.notesItem}>
                          {note.text}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* BOQ Pricing Table */}
        <div className={styles.tableCard}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.th} style={{ width: '40px' }}>
                  ✓
                </th>
                <th className={styles.th} style={{ width: '40px' }}>
                  #
                </th>
                <th className={styles.th}>פריט</th>
                <th className={styles.th} style={{ width: '110px' }}>
                  יחידה
                </th>
                <th className={styles.th} style={{ width: '60px' }}>
                  כמות
                </th>
                <th className={styles.th} style={{ width: '140px' }}>
                  מחיר (₪)
                </th>
                <th className={styles.th} style={{ width: '110px' }}>
                  סה״כ
                </th>
                <th className={styles.th} style={{ minWidth: '160px' }}>
                  הערות
                </th>
              </tr>
            </thead>
            <tbody>
              {groupedLineItems.map(group => {
                let rowIndex = 0;
                return (
                  <React.Fragment key={group.chapterId}>
                    {/* Chapter header */}
                    <tr className={styles.chapterRow}>
                      <td colSpan="8" className={styles.chapterCell}>
                        {group.chapterCode && `${group.chapterCode} - `}
                        {group.chapterName}
                      </td>
                    </tr>

                    {/* Line items */}
                    {group.items.map(({ quoteItem, itemName, totalQty }) => {
                      rowIndex++;
                      const isSkipped = skippedItems.has(quoteItem.id);
                      const dd = quoteItem.display_data || {};
                      const currentCostType = localUnits[quoteItem.id] || dd.cost_type_name || '';
                      const lineTotal = getLineTotal(quoteItem.id, totalQty);
                      const priceVal = localPrices[quoteItem.id] || '';
                      // Show formatted currency when not focused, raw number when focused
                      const displayPrice =
                        focusedPrice === quoteItem.id || !priceVal
                          ? priceVal
                          : formatCurrency(parseFloat(priceVal));

                      return (
                        <tr
                          key={quoteItem.id}
                          className={`${styles.itemRow} ${isSkipped ? styles.skippedRow : ''}`}
                        >
                          {/* Skip toggle */}
                          <td className={styles.td}>
                            <input
                              type="checkbox"
                              checked={!isSkipped}
                              onChange={() => handleToggleSkip(quoteItem.id)}
                              disabled={isReadOnly}
                              className={styles.checkbox}
                              title={isSkipped ? 'הפעל פריט' : 'דלג על פריט'}
                            />
                          </td>

                          {/* Row number */}
                          <td className={`${styles.td} ${styles.rowNum}`}>{rowIndex}</td>

                          {/* Item name + details */}
                          <td className={`${styles.td} ${styles.itemName}`}>
                            {itemName}
                            {dd.option_text && (
                              <div className={styles.itemDetail}>{dd.option_text}</div>
                            )}
                            {dd.item_notes && (
                              <div className={styles.itemDetail}>{dd.item_notes}</div>
                            )}
                          </td>

                          {/* Unit — editable dropdown */}
                          <td className={styles.td}>
                            <select
                              className={styles.unitSelect}
                              value={currentCostType}
                              onChange={e => handleUnitChange(quoteItem.id, e.target.value)}
                              disabled={isReadOnly}
                            >
                              {UNIT_OPTIONS.map(opt => (
                                <option key={opt.name} value={opt.name}>
                                  {opt.label}
                                </option>
                              ))}
                            </select>
                          </td>

                          {/* Quantity */}
                          <td className={`${styles.td} ${styles.qty}`}>{totalQty}</td>

                          {/* Price input — formatted on blur */}
                          <td className={styles.td}>
                            <input
                              type="text"
                              inputMode="decimal"
                              className={`${styles.priceInput} ${isSkipped ? styles.inputDisabled : ''}`}
                              value={isSkipped ? '' : displayPrice}
                              onChange={e => handlePriceChange(quoteItem.id, e.target.value)}
                              onFocus={() => setFocusedPrice(quoteItem.id)}
                              onBlur={() => setFocusedPrice(null)}
                              placeholder=""
                              disabled={isSkipped || isReadOnly}
                            />
                          </td>

                          {/* Line total */}
                          <td className={`${styles.td} ${styles.lineTotal}`}>
                            {!isSkipped && lineTotal > 0 ? formatCurrency(lineTotal) : '—'}
                          </td>

                          {/* Notes input */}
                          <td className={styles.td}>
                            <input
                              type="text"
                              className={styles.notesInput}
                              value={localNotes[quoteItem.id] || ''}
                              onChange={e => handleNotesChange(quoteItem.id, e.target.value)}
                              placeholder="הערה..."
                              disabled={isReadOnly}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>

          {/* Totals row */}
          <div className={styles.totalsSection}>
            <div className={styles.totalRow}>
              <span>סה״כ לפני מע״מ</span>
              <span className={styles.totalAmount}>{formatCurrency(totals.subtotal)}</span>
            </div>
            <div className={styles.totalRow}>
              <span>מע״מ ({(VAT_RATE * 100).toFixed(0)}%)</span>
              <span className={styles.totalAmount}>{formatCurrency(totals.vat)}</span>
            </div>
            <div className={`${styles.totalRow} ${styles.grandTotalRow}`}>
              <span>סה״כ כולל מע״מ</span>
              <span className={styles.totalAmount}>{formatCurrency(totals.grandTotal)}</span>
            </div>
          </div>
        </div>

        {/* Save button */}
        {!isReadOnly && (
          <div className={styles.saveSection}>
            {saveError && <div className={styles.saveError}>{saveError}</div>}
            {saveSuccess && (
              <div className={styles.saveSuccessMsg}>
                <Check size={18} />
                נשמר בהצלחה
              </div>
            )}
            <button className={styles.saveButton} onClick={handleSave} disabled={isSaving}>
              <FloppyDisk size={20} />
              {isSaving ? 'שומר...' : 'שמור הצעת מחיר'}
            </button>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className={styles.footer}>
        <p>מופעל על ידי Kamuyot</p>
      </footer>
    </div>
  );
};

export default PublicBOQQuoteView;
